import React, { useState, ReactElement, createContext, useContext } from 'react';
import { Toast, ToastProps } from './toast';
import { ToastSettings } from './types';
import { defaultToastSettings } from './constants';
import { ToastContainer } from './toast-container';
import classnames from 'classnames';

export type ToastContextType = [(props: Partial<ToastProps>) => void, (id: string) => void];

export const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const sizes = ['medium', 'auto'] as const;
export type ToastSize = typeof sizes[number];

export const positions = ['left', 'center', 'right'] as const;
export type ToastPosition = typeof positions[number];

/**
 * Returns a tuple, where
 *
 * - The first element is a function to add a toast with the provided properties.
 * - The second element is a function to remove a toast by its unique ID.
 *
 */
export const useToastContext = () => {
  const ctx = useContext(ToastContext);

  if (!ctx) {
    throw new Error('Toast Context should be used inside the ToastContextProvider');
  }
  return ctx;
};

type DefaultSettings = Partial<ToastSettings> & {
  size?: ToastSize;
  position?: ToastPosition;
};

type ToastContextProviderProps = {
  defaultSettings?: DefaultSettings;
} & React.HtmlHTMLAttributes<HTMLDivElement>;

// used to track the displayed toast count
// for unique auto-incrementing id
let toastCount = 0;

/**
 * A context provider that serves as a container for Toast notifications.
 *
 * Render at the root level of your application to ensure
 * that toast notifications overlay other UI elements.
 *
 * The provider accepts default settings that apply to all toasts.
 * You can override these defaults individually when triggering a new toast.
 *
 * Default Settings:
 * - variant: 'default'
 * - hasIcon: false
 * - size: 'medium'
 * - position: 'center'
 * - autoClose: 2500
 * - closeOnClick: false
 *
 * @example Usage:
 *
 * ```tsx
 * // At the root of your app
 * const App = () => (
 *   <ToastContextProvider defaultSettings={{ hasIcon: true, position:'left' }}>
 *      <App />
 *   </ToastContextProvider>
 * );
 *
 * // Within any component in your app
 * const MyComponent = () => {
 *   const [addToast] = useToastContext();
 *   return <button onClick={() => addToast({ children: <h1>My Toast</h1>, variant: 'information' })}>Show Toast</button>;
 * };
 * ```
 */
export const ToastContextProvider = ({
  children,
  className,
  defaultSettings = {},
  ...props
}: ToastContextProviderProps): ReactElement => {
  const [toasts, setToasts] = useState<ToastProps[]>([]);
  const { size, position, ...settings } = defaultSettings;
  const containerSize = size || 'medium';
  const containerPosition = position || 'center';

  const classNames = classnames(
    className,
    'airtame-toast',
    `airtame-toast--${containerSize}`,
    `airtame-toast--${containerPosition}`
  );

  const toastSettings = {
    ...defaultToastSettings,
    ...settings,
  };

  const addToast = (props: Partial<Omit<ToastProps, 'id'>>) => {
    const id = toastCount;
    toastCount += 1;

    const toast: ToastProps = {
      ...toastSettings,
      ...props,
      id: id.toString(),
    };

    setToasts((prevToasts) => [toast, ...prevToasts]);
  };

  const removeToast = (id: string) => {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
  };

  const value: ToastContextType = [addToast, removeToast];

  const contextValue = useContext(ToastContext);

  if (contextValue !== undefined) {
    throw new Error(
      'Nested ToastContextProvider instances detected. Ensure ToastContextProvider is used as a singleton.'
    );
  }

  return (
    <ToastContext.Provider value={value}>
      {children}

      <ToastContainer className={classNames} {...props}>
        {toasts.map((toast) => (
          <Toast {...toast} key={toast.id} />
        ))}
      </ToastContainer>
    </ToastContext.Provider>
  );
};
